import { useMutation } from "@apollo/client";
import { MembershipBillingChoice, MembershipBillingChoiceInput } from "@app/shared/types";
import { analyticsTrack } from "app/analytics/track";
import { isRunningCypress } from "app/config";
import { GRAPHQL_MUTATION_CREATE_MEMBERSHIP_CHECKOUT_SESSION } from "app/queries";
import { routes } from "app/routes";
import { getUtmParametersFromLocalStorage, UtmPrefix } from "app/utmParameters";
import { showSnackbarAlertOnRedirect } from "components/AlertSnackBar";
import { push } from "connected-react-router";
import { COMMITTED_INTEREST_GROUP_FIELD } from "features/member/interestGroups/constants";
import { selectReferralCode } from "features/navigation/navigationSlice";
import LoadingPage from "features/pages/LoadingPage";
import { useAffiliatePartnerTracking } from "hooks/useAffiliatePartnerTracking";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckoutPage } from "./CheckoutPage";
import { SESSION_STORAGE_SCHOLARSHIP_APPLICATION_ID_KEY } from "./SignupBillingStep";
import { getCurrentCohort, isUserInFreeTrialSession } from "./signupHelpers";

interface StripeClientSecretProviderProps {
    billingChoice: MembershipBillingChoice;
}

export const StripeClientSecretProvider = (props: StripeClientSecretProviderProps) => {
    const dispatch = useDispatch();

    const { billingChoice } = props;

    const referralCode = useSelector(selectReferralCode);
    const affiliatePartner = useAffiliatePartnerTracking();

    const cohort = getCurrentCohort();
    const isFreeTrialSignupFlow = isUserInFreeTrialSession();

    const [generateCheckoutSession] = useMutation(
        GRAPHQL_MUTATION_CREATE_MEMBERSHIP_CHECKOUT_SESSION,
    );

    const [clientSecret, setClientSecret] = useState("");

    const onCompleteBilling = async () => {
        const billingChoiceInput: MembershipBillingChoiceInput = {
            planName: billingChoice.plan.planName,
            donationAmount: billingChoice.donationAmount ?? 0,
        };

        const referredByPartnerId = affiliatePartner?.partnerId;

        try {
            const result = await generateCheckoutSession({
                variables: {
                    billingChoice: billingChoiceInput,
                    referralCode,
                    utmParameters: getUtmParametersFromLocalStorage(),
                    affiliateUtmParameters: getUtmParametersFromLocalStorage(UtmPrefix.AFFILIATE),
                    scholarshipApplicationId:
                        sessionStorage.getItem(SESSION_STORAGE_SCHOLARSHIP_APPLICATION_ID_KEY) ||
                        undefined,
                    referredByPartnerId,
                    isFreeTrial: isFreeTrialSignupFlow,
                    isDropIn: window.sessionStorage.getItem("isDropIn") === "true",
                    cohort: cohort,
                },
            });
            const { sessionId, clientSecret } = result.data.createMembershipCheckoutSession;
            setClientSecret(clientSecret);
            if (isRunningCypress()) {
                (window as any).stripeSessionId = sessionId;
            }
        } catch (e) {
            if (e instanceof Error) {
                if (e.message?.includes("User already has active/pending memberships")) {
                    analyticsTrack("checkout.payment.active-membership");
                    showSnackbarAlertOnRedirect("You already have an active membership");
                    dispatch(push(routes.memberZone()));
                } else {
                    analyticsTrack("checkout.payment.unknown-error", { message: e.message });
                }
            } else {
                analyticsTrack("checkout.payment.unknown-error", { message: e });
            }
        }
    };

    useEffect(() => {
        onCompleteBilling();
    }, []);

    if (!clientSecret) {
        return <LoadingPage />;
    } else {
        return <CheckoutPage clientSecret={clientSecret} billingChoice={billingChoice} />;
    }
};
